/**
 * BackupAlertsForm — Batch #14.339 : alertes (courriel / SMS) si une sauvegarde échoue ou manque depuis N h,
 * et rappel mensuel de copie hors ligne (jour du mois). Bouton « Envoyer un test ».
 */
import { useEffect, useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Bell, HardDrive, Send } from "lucide-react";

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white";

export default function BackupAlertsForm({ settings, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    setF({ alert_email: settings.alert_email || "", alert_sms: settings.alert_sms || "", alert_on_failure: settings.alert_on_failure !== false, alert_missed_hours: settings.alert_missed_hours || 48,
      offline_reminder_enabled: settings.offline_reminder_enabled !== false, offline_reminder_day: settings.offline_reminder_day || 1 });
  }, [settings]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/backup/settings", { ...f, alert_missed_hours: Number(f.alert_missed_hours), offline_reminder_day: Number(f.offline_reminder_day) });
      toast.success("Alertes enregistrées"); onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const r = await api.post("/admin/backup/alerts/test"); toast.success(`Test envoyé par ${r.data.channels.join(" + ")}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Envoi du test impossible"); }
    finally { setTesting(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4" data-testid="backup-alerts">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Bell className="w-4 h-4 text-amber-600" /> Alertes et rappel de copie hors ligne</h3>
        <button onClick={test} disabled={testing} className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50" data-testid="backup-alert-test">
          <Send className="w-3.5 h-3.5" /> {testing ? "Envoi…" : "Envoyer un test"}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="text-xs text-slate-600">Courriel d'alerte
          <input type="email" value={f.alert_email || ""} onChange={(e) => set("alert_email", e.target.value)} placeholder="vide = courriel du cabinet" className={inputCls} data-testid="backup-alert-email" />
        </label>
        <label className="text-xs text-slate-600">SMS d'alerte (optionnel)
          <input value={f.alert_sms || ""} onChange={(e) => set("alert_sms", e.target.value)} placeholder="+1 514 555 0199" className={inputCls} data-testid="backup-alert-sms" disabled={!settings.sms_configured} />
          {!settings.sms_configured && <span className="text-[11px] text-slate-400">Twilio non configuré</span>}
        </label>
        <label className="text-xs text-slate-600">Alerte si aucune sauvegarde depuis
          <select value={f.alert_missed_hours ?? 48} onChange={(e) => set("alert_missed_hours", e.target.value)} className={inputCls} data-testid="backup-alert-hours">
            {[24, 36, 48, 72, 168].map((h) => <option key={h} value={h}>{h} h{h === 168 ? " (1 semaine)" : ""}</option>)}
          </select>
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={!!f.alert_on_failure} onChange={(e) => set("alert_on_failure", e.target.checked)} data-testid="backup-alert-failure" /> M'alerter dès qu'une sauvegarde planifiée échoue
      </label>
      <div className="border-t border-slate-100 pt-3 flex flex-wrap items-end gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={!!f.offline_reminder_enabled} onChange={(e) => set("offline_reminder_enabled", e.target.checked)} data-testid="backup-offline-enabled" />
          <HardDrive className="w-4 h-4 text-slate-500" /> Rappel mensuel : télécharger une copie sur un disque externe
        </label>
        <label className="text-xs text-slate-600">Jour du mois
          <input type="number" min="1" max="28" value={f.offline_reminder_day ?? 1} onChange={(e) => set("offline_reminder_day", e.target.value)} className={`${inputCls} w-24`} data-testid="backup-offline-day" disabled={!f.offline_reminder_enabled} />
        </label>
        <div className="flex-1" />
        <button onClick={save} disabled={busy} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50" data-testid="backup-alerts-save">Enregistrer les alertes</button>
      </div>
    </div>
  );
}
